"use client";

import { useState, useMemo, useEffect } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from "@/components/ui/carousel";

interface ProductGalleryProps {
    images: string[];
    alt?: string;
}

export const ProductGallery = ({
    images,
    alt = "Product image",
}: ProductGalleryProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [api, setApi] = useState<CarouselApi>();

    // ✅ Nettoyage propre sans double filter
    const safeImages = useMemo(() => {
        const filtered = images?.filter(
            (img) => img && img.trim() !== ""
        );
        return filtered && filtered.length > 0
            ? filtered
            : ["/placeholder.png"];
    }, [images]);

    // ✅ Sécurité si images changent dynamiquement
    useEffect(() => {
        if (activeIndex >= safeImages.length) {
            setActiveIndex(0);
        }
    }, [activeIndex, safeImages.length]);

    // ✅ Auto-scroll logic
    useEffect(() => {
        if (isPaused || safeImages.length <= 1) return;

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % safeImages.length);
        }, 4000); // Cycle every 4 seconds

        return () => clearInterval(interval);
    }, [isPaused, safeImages.length]);

    // ✅ Sync thumbnail carousel with active index
    useEffect(() => {
        if (!api) return;
        api.scrollTo(activeIndex);
    }, [api, activeIndex]);

    return (
        <div
            className="space-y-4"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* MAIN IMAGE */}
            <div className="relative w-full max-w-[650px] aspect-square rounded-[28px] overflow-hidden border border-gray-200 bg-white">
                <Image
                    src={safeImages[activeIndex] || "/placeholder.png"}
                    alt={alt}
                    fill
                    priority
                    className="object-contain transition-opacity duration-500"
                />
            </div>

            {/* THUMBNAILS CAROUSEL */}
            {safeImages.length > 1 && (
                <div className="max-w-[650px] px-0">
                    <Carousel
                        setApi={setApi}
                        opts={{
                            align: "start",
                            containScroll: "trimSnaps",
                        }}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-3 mt-4">
                            {safeImages.map((img, index) => (
                                <CarouselItem key={`${img}-${index}`} className="pl-3 basis-1/4 sm:basis-1/5">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveIndex(index);
                                            setIsPaused(true); // Stop auto-scroll on manual select
                                        }}
                                        className={cn(
                                            "relative w-full aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 bg-white",
                                            activeIndex === index
                                                ? "border-primary-500 shadow-md scale-95"
                                                : "border-gray-100 hover:border-gray-300"
                                        )}
                                    >
                                        <Image
                                            src={img}
                                            alt={`${alt} thumbnail ${index + 1}`}
                                            fill
                                            className="object-contain p-1"
                                        />
                                    </button>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            )}
        </div>
    );
};
